import { ColorSpaceCpu, renderHSLAndColorGrading, RenderOptions } from "./renderer-cpu"
import { Adjustments } from "@/core/types"
import { renderCpu } from "./renderer-cpu"
import { vec3 } from "gl-matrix"
import { applyTransform, rgbToHsv } from "./color-space"
import { clamp } from "@/core/utils/math"
import { hueShiftScaleMap } from "@/core/hsl"
import {
	capitalize,
	getDecreasedValue,
	getWeightedDiff,
	logColorMatch,
	wrapHueDiff,
	HueColor,
	ImageSatLumStats,
	ColorStats,
} from "./utils"
import { PixelData } from "@/core/utils/image-data"
import { computeHistogram, computePercentiles } from "../calibration/ai-calibration-brightness"

export function hueToColor(hue: number): [string | null, string | null, number] {
	hue = clamp(hue, 0, 1)
	hue = Math.round(hue * 360)
	if (hue === 360) {
		hue = 0
	}

	const hues = [0, 30, 55, 95, 160, 230, 270, 330, 360]
	const inversePointsMap = [
		"red",
		"orange",
		"yellow",
		"green",
		"aqua",
		"blue",
		"purple",
		"magenta",
		"red",
	]

	for (let i = 0; i < hues.length - 1; i++) {
		const hue1 = hues[i] ?? 0
		const hue2 = hues[i + 1] ?? 0
		if (hue >= hue1 && hue < hue2) {
			const fraction = (hue - hue1) / (hue2 - hue1)
			const color1 = inversePointsMap[i] ?? null
			const color2 = inversePointsMap[i + 1] ?? null
			return [color1, color2, fraction]
		}
	}

	return [null, null, 0]
}

export function getHSLDeltaFromDiffs(options: {
	hsvDiff: vec3
	color: string
	scale: number
	satLumScale?: number
	finalHueScale?: number
	limit?: number
}): vec3 {
	const { hsvDiff, color, scale } = options
	const satLumScale = options.satLumScale ?? 0.06
	const finalHueScale = options.finalHueScale ?? scale
	const limit = options.limit ?? 0.5
	const [hueDiff, satDiff, lumDiff] = hsvDiff
	const hueScale = hueShiftScaleMap[color.toLowerCase()]

	// Calculate hue adjustment
	const hueDelta = hueDiff < 0 ? hueDiff / (hueScale[0] * 0.09) : hueDiff / (hueScale[1] * 0.09)

	// Calculate luminance adjustment
	const lumScale = lumDiff < 0 ? 0.25 : 0.2
	const lumDelta = clamp(lumDiff / lumScale, -limit, limit)

	// Calculate saturation adjustment
	const adjustedSatDiff = satDiff + satLumScale * lumDelta
	const satScale = adjustedSatDiff < 0 ? 0.6 : 0.3

	// Apply scale and store results
	const deltaHue = clamp(hueDelta, -limit, limit) * finalHueScale
	const deltaLum = lumDelta * scale
	const deltaSat = clamp(adjustedSatDiff / satScale, -limit, limit) * scale

	return [deltaHue, deltaSat, deltaLum]
}

function mergeHSL(
	adjustments: Partial<Adjustments>,
	delta: Partial<Adjustments>,
): Partial<Adjustments> {
	const adjustmentsCopy = { ...adjustments }
	const hueLimit = 0.4
	const satLimit = 0.35
	const lumLimit = 0.35

	for (const color of Object.keys(hueShiftScaleMap)) {
		const colorName = capitalize(color)
		const hueKey = `hue${colorName}`
		const satKey = `saturation${colorName}`
		const lumKey = `luminance${colorName}`

		adjustmentsCopy[hueKey] = clamp(
			((adjustments[hueKey] ?? 0) as number) + ((delta[hueKey] ?? 0) as number),
			-hueLimit,
			hueLimit,
		)
		adjustmentsCopy[satKey] = clamp(
			((adjustments[satKey] ?? 0) as number) + ((delta[satKey] ?? 0) as number),
			-1,
			satLimit,
		)
		adjustmentsCopy[lumKey] = clamp(
			((adjustments[lumKey] ?? 0) as number) + ((delta[lumKey] ?? 0) as number),
			-lumLimit,
			lumLimit,
		)
	}

	return adjustmentsCopy
}

interface HSVDiffs {
	[color: string]: [number, number, number]
}

interface PixelColor {
	color1: string
	color2: string
	fraction: number
	pixelIdx: number
	satWeight: number
}

function getPixelColorMap(
	hsv: Float32Array,
	satThreshold: number,
	lumThreshold: number,
): PixelColor[] {
	const pixelColorMap: PixelColor[] = []
	const validPixels: number[] = []
	const totalPixels = hsv.length / 3

	for (let i = 0; i < totalPixels; i++) {
		if ((hsv[i * 3 + 1] ?? 0) > satThreshold && (hsv[i * 3 + 2] ?? 0) > lumThreshold) {
			validPixels.push(i)
		}
	}

	if (validPixels.length < 5) {
		logColorMatch("[getPixelColorMap] mask size too small", validPixels.length, {
			satThreshold,
			lumThreshold,
		})
		return []
	}

	for (let i = 0; i < validPixels.length; i++) {
		const pixelIdx = validPixels[i] as number
		const hue = hsv[pixelIdx * 3] as number
		const [color1, color2, fraction] = hueToColor(hue)
		if (!color1 || !color2) continue
		const satWeight = Math.min((hsv[pixelIdx * 3 + 1] ?? 0) + 0.15, 1)

		pixelColorMap.push({ color1, color2, fraction, pixelIdx, satWeight })
	}

	return pixelColorMap
}

function valSatToLuminance(value: number, saturation: number): number {
	// L = V * (1 - S/2)
	return value * (1 - saturation / 2)
}

function getHueSatDiffNew(options: {
	hsvCurrent: Float32Array
	hsvAfter: Float32Array
	pixelWeights: number[]
	satThreshold: number
	lumThreshold: number
	pixelColorMap?: PixelColor[]
	minWeight?: number
}): HSVDiffs {
	const {
		hsvCurrent,
		hsvAfter,
		pixelWeights,
		satThreshold,
		lumThreshold,
		minWeight = 0.005,
	} = options
	let pixelColorMap = options.pixelColorMap
	if (!pixelColorMap) {
		pixelColorMap = getPixelColorMap(hsvCurrent, satThreshold, lumThreshold)
	}

	// Initialize maps for differences and weights
	const diffMap: HSVDiffs = {}
	const weights: { [color: string]: number } = {}

	// Process each valid pixel
	for (let i = 0; i < pixelColorMap.length; i++) {
		const pixel = pixelColorMap[i] as PixelColor
		const { color1, color2, fraction, pixelIdx, satWeight } = pixel

		const adjustedFraction = clamp((fraction - 0.05) / 0.9, 0, 1)

		// Get existing differences or initialize new ones
		const existedDiff1 = diffMap[color1] ?? [0, 0, 0]
		const existedDiff2 = diffMap[color2] ?? [0, 0, 0]

		// Calculate differences

		let hueDiff = wrapHueDiff((hsvAfter[pixelIdx * 3] ?? 0) - (hsvCurrent[pixelIdx * 3] ?? 0))
		const hueScale1 = hueShiftScaleMap[color1]
		const hueScale2 = hueShiftScaleMap[color2]
		const maxHueScale = Math.max(...hueScale1, ...hueScale2)
		if (Math.abs(hueDiff) * 360 > maxHueScale * 30 * 1.25) {
			// do not trust unreasonably high hue diff
			continue
		}

		// if after saturation is very low, reduce hue shift weight
		const valAfter = hsvAfter[pixelIdx * 3 + 2] ?? 0
		const valWeight = 1 - clamp((0.15 - valAfter) / 0.15, 0, 1) * 0.7
		const afterSat = hsvAfter[pixelIdx * 3 + 1] ?? 0
		let hueShiftWeight = (1 - clamp((0.3 - afterSat) / 0.2, 0, 1)) * valWeight
		hueDiff *= hueShiftWeight
		hueShiftWeight = 0.6 + hueShiftWeight * 0.4

		const satDiff = (hsvAfter[pixelIdx * 3 + 1] ?? 0) - (hsvCurrent[pixelIdx * 3 + 1] ?? 0)
		//const valueDiff = (hsvAfter[pixelIdx * 3 + 2] ?? 0) - (hsvCurrent[pixelIdx * 3 + 2] ?? 0)
		const lumAfter = valSatToLuminance(
			hsvAfter[pixelIdx * 3 + 2] ?? 0,
			hsvAfter[pixelIdx * 3 + 1] ?? 0,
		)
		const lumBefore = valSatToLuminance(
			hsvCurrent[pixelIdx * 3 + 2] ?? 0,
			hsvCurrent[pixelIdx * 3 + 1] ?? 0,
		)
		const lumDiff = lumAfter - lumBefore

		// Calculate weights
		const pixelWeight = pixelWeights[pixelIdx] ?? 0
		const w1 = pixelWeight * (1 - adjustedFraction) * satWeight * hueShiftWeight
		const w2 = pixelWeight * adjustedFraction * satWeight * hueShiftWeight

		// Update diff_map and weights based on fraction
		if (adjustedFraction < 0.3) {
			diffMap[color1] = [
				existedDiff1[0] + hueDiff * w1,
				existedDiff1[1] + satDiff * w1,
				existedDiff1[2] + lumDiff * w1,
			]
			weights[color1] = (weights[color1] ?? 0) + w1
		} else if (adjustedFraction > 0.7) {
			diffMap[color2] = [
				existedDiff2[0] + hueDiff * w2,
				existedDiff2[1] + satDiff * w2,
				existedDiff2[2] + lumDiff * w2,
			]
			weights[color2] = (weights[color2] ?? 0) + w2
		} else {
			diffMap[color1] = [
				existedDiff1[0] + hueDiff * w1,
				existedDiff1[1] + satDiff * w1,
				existedDiff1[2] + lumDiff * w1,
			]
			diffMap[color2] = [
				existedDiff2[0] + hueDiff * w2,
				existedDiff2[1] + satDiff * w2,
				existedDiff2[2] + lumDiff * w2,
			]
			weights[color1] = (weights[color1] ?? 0) + w1
			weights[color2] = (weights[color2] ?? 0) + w2
		}
	}
	logColorMatch("[getHueSatDiffNew] weights", weights)

	// Calculate final results
	const result: HSVDiffs = {}
	for (const [color, [hueDiff, satDiff, lumDiff]] of Object.entries(diffMap)) {
		const w = Math.max(weights[color] ?? 0, minWeight)
		result[color] = [hueDiff / w, satDiff / w, lumDiff / w]
	}

	logColorMatch("[getHueSatDiffNew] result", result)
	return result
}

function getHSLDeltaNew(options: {
	prophotoCurrent: Float32Array
	hsvAfter: Float32Array
	pixelWeights: number[]
	scale: number
	pixelColorMap?: PixelColor[]
}): { hslDelta: Partial<Adjustments>; pixelColorMap: PixelColor[] } {
	const { prophotoCurrent, hsvAfter, pixelWeights, scale } = options
	const satThreshold = 0.2
	const lumThreshold = 0.04 // 0.2

	const hsvCurrent = applyTransform(prophotoCurrent, rgbToHsv)

	let pixelColorMap = options.pixelColorMap
	if (!pixelColorMap) {
		pixelColorMap = getPixelColorMap(hsvCurrent, satThreshold, lumThreshold)
	}
	// test always use pixelColorMap
	// const pixelColorMap = getPixelColorMap(hsvCurrent, satThreshold, lumThreshold)

	const hsvDiffMap = getHueSatDiffNew({
		hsvCurrent,
		hsvAfter,
		pixelWeights,
		satThreshold,
		lumThreshold,
		pixelColorMap,
	})
	const hslDelta: Partial<Adjustments> = {}

	for (const [color, hsvDiff] of Object.entries(hsvDiffMap)) {
		const [deltaHue, deltaSat, deltaLum] = getHSLDeltaFromDiffs({
			hsvDiff,
			color,
			scale,
			satLumScale: 0.08,
			finalHueScale: 0.7,
			limit: 0.4,
		})

		const colorName = capitalize(color)
		hslDelta[`hue${colorName}`] = deltaHue
		hslDelta[`luminance${colorName}`] = deltaLum
		hslDelta[`saturation${colorName}`] = deltaSat
	}

	return { hslDelta, pixelColorMap }
}

export function getHSL(options: {
	prophotoCurrent: Float32Array
	prophotoAfter: Float32Array
	pixelWeights: number[]
	adjustments: Partial<Adjustments>
	baseRenderOptions: RenderOptions
	isCloseToBw: boolean
	averageHSL?: Partial<Adjustments>
}): Partial<Adjustments> {
	const { prophotoAfter, pixelWeights, adjustments, baseRenderOptions, isCloseToBw, averageHSL } =
		options

	const hsvAfter = applyTransform(prophotoAfter, rgbToHsv)

	const prophotoInput = options.prophotoCurrent
	let prophotoCurrent = prophotoInput

	let lastDiffAll = getWeightedDiff(prophotoCurrent, prophotoAfter, pixelWeights)
	let diffAll = lastDiffAll
	let iterations = 0
	let finalAdjustments = adjustments
	const initialDiffAll = lastDiffAll

	logColorMatch("[getHSLNew] initial diff_all", lastDiffAll)

	let pixelColorMap: PixelColor[] | undefined = undefined

	while (diffAll > 0.002 && iterations < 5) {
		const result = getHSLDeltaNew({
			prophotoCurrent,
			hsvAfter,
			pixelWeights,
			scale: 0.7,
			pixelColorMap,
		})
		const adjustmentsCopy = mergeHSL(finalAdjustments, result.hslDelta)
		limitHSL(adjustmentsCopy, averageHSL)
		pixelColorMap = result.pixelColorMap

		// note: adjustmentsCopy shouldn't contain any color grading adjustments if we want to render HSL only
		prophotoCurrent = renderHSLAndColorGrading(
			prophotoInput,
			adjustmentsCopy,
			baseRenderOptions,
		)
		diffAll = getWeightedDiff(prophotoCurrent, prophotoAfter, pixelWeights)

		logColorMatch(
			"[getHSLNew] diff_all",
			diffAll,
			", iterations:",
			iterations,
			", hslDelta:",
			result.hslDelta,
		)

		// run at least 1 iteration
		if (iterations > 0 && diffAll > lastDiffAll - 0.00003) {
			break
		}

		lastDiffAll = diffAll
		iterations++
		finalAdjustments = { ...adjustmentsCopy }
	}

	if (lastDiffAll > initialDiffAll + 0.005) {
		finalAdjustments = adjustments
	}

	// reduce red/orange hue and sat as it tends to make face tone not natural
	if (finalAdjustments.hueRed != undefined && Math.abs(finalAdjustments.hueRed) > 0.15) {
		finalAdjustments.hueRed = getDecreasedValue(finalAdjustments.hueRed, 0.15, 0.5)
	}
	if (
		finalAdjustments.saturationRed != undefined &&
		((!isCloseToBw && finalAdjustments.saturationRed < -0.15) ||
			finalAdjustments.saturationRed > 0.15)
	) {
		finalAdjustments.saturationRed = getDecreasedValue(
			finalAdjustments.saturationRed,
			0.15,
			0.5,
		)
	}
	if (finalAdjustments.hueOrange != undefined && Math.abs(finalAdjustments.hueOrange) > 0.15) {
		finalAdjustments.hueOrange = getDecreasedValue(finalAdjustments.hueOrange, 0.15, 0.5)
	}
	if (
		finalAdjustments.saturationOrange != undefined &&
		((!isCloseToBw && finalAdjustments.saturationOrange < -0.15) ||
			finalAdjustments.saturationOrange > 0.15)
	) {
		finalAdjustments.saturationOrange = getDecreasedValue(
			finalAdjustments.saturationOrange,
			0.15,
			0.5,
		)
	}

	return finalAdjustments
}

function limitHSL(adjustments: Partial<Adjustments>, averageHSL?: Partial<Adjustments>) {
	if (!averageHSL) return

	for (const color of Object.keys(hueShiftScaleMap)) {
		const colorName = capitalize(color)
		const hueKey = `hue${colorName}`
		const satKey = `saturation${colorName}`
		const lumKey = `luminance${colorName}`

		const averageHue = averageHSL[hueKey] ?? 0
		const averageSat = averageHSL[satKey] ?? 0
		const averageLum = averageHSL[lumKey] ?? 0

		const hue = adjustments[hueKey] ?? averageHue
		const sat = adjustments[satKey] ?? averageSat
		const lum = adjustments[lumKey] ?? averageLum

		adjustments[hueKey] = clamp(
			averageHue + 0.5 * (hue - averageHue),
			averageHue - 0.04,
			averageHue + 0.04,
		)
		adjustments[satKey] = clamp(
			averageSat + 0.5 * (sat - averageSat),
			averageSat - 0.1,
			averageSat + 0.1,
		)
		adjustments[lumKey] = clamp(
			averageLum + 0.5 * (lum - averageLum),
			averageLum - 0.1,
			averageLum + 0.1,
		)
	}
}

interface CalibrationAdjustments {
	calibrationRedHue?: number
	calibrationRedSat?: number
	calibrationGreenHue?: number
	calibrationGreenSat?: number
	calibrationBlueHue?: number
	calibrationBlueSat?: number
}

function getCalibrationDelta(
	prophotoCurrent: Float32Array,
	prophotoAfter: Float32Array,
	pixelWeights: number[],
	includeSat: boolean = false,
	scale: number = 1,
	satLimit: number = 0.2,
): CalibrationAdjustments {
	const hsvCurrent = applyTransform(prophotoCurrent, rgbToHsv)
	const hsvAfter = applyTransform(prophotoAfter, rgbToHsv)

	const hsvDiffMap = getHueSatDiffNew({
		hsvCurrent,
		hsvAfter,
		pixelWeights,
		satThreshold: 0.3,
		lumThreshold: 0.15, // 0.3
		minWeight: 0.02,
	})
	const calibration: CalibrationAdjustments = {}

	const colorMap = {
		orange: "blue",
		yellow: "blue",
	}
	for (const color of ["red", "green", "blue", "orange", "yellow"]) {
		const diffs = hsvDiffMap[color]
		if (!diffs) continue

		const [hueDiff, satDiff] = diffs

		// Calculate normalized adjustments
		const hue = clamp(hueDiff / 0.12, -1, 1)
		const sat = includeSat ? clamp(satDiff / 0.2, -satLimit, satLimit) : 0

		const colorName = capitalize(colorMap[color] ?? color)

		// for blue/orange/yellow, take the larger one if same sign, otherwise take the average
		const existingHue = calibration[`calibration${colorName}Hue`]
		let newHue = hue * scale
		if (existingHue != undefined) {
			if (existingHue * newHue > 0) {
				newHue = Math.max(Math.abs(existingHue), Math.abs(newHue)) * Math.sign(existingHue)
			} else {
				newHue = (existingHue + newHue) * 0.5
			}
		}
		calibration[`calibration${colorName}Hue`] = newHue
		calibration[`calibration${colorName}Sat`] = sat * scale
	}

	// If green hue is set, check if yellow saturation diff is aligned; higher green hue, lower yellow sat
	const greenHue = calibration.calibrationGreenHue
	if (greenHue != undefined) {
		const yellowSatDiff = hsvDiffMap["yellow"]?.[1]
		if (yellowSatDiff != undefined) {
			const newGreenHue = -yellowSatDiff / 0.3
			if (newGreenHue * greenHue > 0) {
				calibration.calibrationGreenHue =
					Math.min(Math.abs(newGreenHue), Math.abs(greenHue)) * Math.sign(greenHue)
			} else {
				calibration.calibrationGreenHue = (greenHue + newGreenHue) * 0.5
			}
			logColorMatch(
				"[getCalibrationDelta] greenHue",
				greenHue,
				"->",
				calibration.calibrationGreenHue,
				{ yellowSatDiff, newGreenHue },
			)
		}
	}

	return calibration
}

export function getCalibration(options: {
	prophotoCurrent: Float32Array
	prophotoAfter: Float32Array
	toneMapInvBefore: Float32Array
	pixelWeights: number[]
	adjustments: Partial<Adjustments>
	baseRenderOptions: RenderOptions
	limit?: number
}): Partial<Adjustments> {
	const {
		prophotoAfter,
		toneMapInvBefore,
		pixelWeights,
		adjustments,
		baseRenderOptions,
		limit = 0.5,
	} = options
	let prophotoCurrent = options.prophotoCurrent

	const renderOptions = {
		...baseRenderOptions,
		inputColorSpace: ColorSpaceCpu.toneMapInversed,
		outputColorSpace: ColorSpaceCpu.proPhoto,
	}

	// Iteratively refine calibration
	let lastDiffAll = getWeightedDiff(prophotoCurrent, prophotoAfter, pixelWeights)
	let diffAll = lastDiffAll
	const initialDiffAll = lastDiffAll
	let iterations = 0

	logColorMatch("[calibration] initial diff_all", diffAll)

	// Create deep copy of adjustments
	const adjustmentsCopy = structuredClone(adjustments)
	let result = adjustmentsCopy

	while (diffAll > 0.005 && iterations < 5) {
		// Enable saturation adjustment after 3 iterations
		const includeSat = iterations >= 3
		const scale = iterations === 0 ? 0.3 : 0.5

		const calibration = getCalibrationDelta(
			prophotoCurrent,
			prophotoAfter,
			pixelWeights,
			includeSat,
			scale,
		)

		// Apply new adjustments with limits
		for (const [key, value] of Object.entries(calibration)) {
			adjustmentsCopy[key] = clamp((adjustmentsCopy[key] ?? 0) + value, -limit, limit)
		}

		// Render with current adjustments
		prophotoCurrent = renderCpu(toneMapInvBefore, adjustmentsCopy, renderOptions)
		diffAll = getWeightedDiff(prophotoCurrent, prophotoAfter, pixelWeights)

		logColorMatch("[calibration] diff_all:", diffAll, ", iterations:", iterations)
		logColorMatch("[calibration] deltaResult:", calibration)

		// Check convergence conditions; skip checking the first iteration
		if (iterations > 0 && diffAll > lastDiffAll - 0.0001) {
			break
		}

		iterations++
		lastDiffAll = diffAll
		result = { ...adjustmentsCopy }
	}

	if (lastDiffAll > initialDiffAll) {
		result = adjustments
	}

	return result
}

function imageDataToFloat32Array(imageData: ImageData | PixelData, maxSide = 160): Float32Array {
	const data = imageData.data

	if ((imageData as PixelData).yuvDataLayout) {
		throw new Error("Cannot convert YUV data to floating point array.")
	}

	let length = data.length / 4

	let step = 1
	if (length > maxSide * maxSide) {
		step = Math.max(1, Math.round(length / (maxSide * maxSide)))
		length = Math.floor(length / step)
	}
	const array = new Float32Array(length * 3)
	for (let i = 0; i < length; i += 1) {
		array[i * 3] = (data[i * 4 * step] ?? 0) / 255.0
		array[i * 3 + 1] = (data[i * 4 * step + 1] ?? 0) / 255.0
		array[i * 3 + 2] = (data[i * 4 * step + 2] ?? 0) / 255.0
	}
	return array
}

export function getSaturationStats(
	imageData: ImageData | PixelData | Float32Array,
	pixelWeights?: number[],
): ImageSatLumStats {
	if (!(imageData instanceof Float32Array)) {
		imageData = imageDataToFloat32Array(imageData)
	}
	const hsv = applyTransform(imageData, rgbToHsv)

	const totalPixels = hsv.length / 3
	let totalSat = 0
	let totalWeight = 0
	let totalLum = 0
	let totalLumWeight = 0

	// Initialize counters for each hue region
	const hueRegions: Record<HueColor, { sum: number; weight: number; max: number }> = {
		red: { sum: 0, weight: 0, max: 0 },
		orange: { sum: 0, weight: 0, max: 0 },
		yellow: { sum: 0, weight: 0, max: 0 },
		green: { sum: 0, weight: 0, max: 0 },
		aqua: { sum: 0, weight: 0, max: 0 },
		blue: { sum: 0, weight: 0, max: 0 },
		purple: { sum: 0, weight: 0, max: 0 },
		magenta: { sum: 0, weight: 0, max: 0 },
		gray: { sum: 0, weight: 0, max: 0 },
	}

	const luminances: number[] = []
	// Process each pixel
	for (let i = 0; i < totalPixels; i++) {
		const hue = hsv[i * 3] ?? 0
		const sat = hsv[i * 3 + 1] ?? 0
		const val = hsv[i * 3 + 2] ?? 0
		const weight = pixelWeights?.[i] ?? 1

		const lum = valSatToLuminance(val, sat)
		luminances.push(lum)
		totalLum += lum * weight
		totalLumWeight += weight

		if (lum < 0.05 || lum > 0.95) {
			continue
		}

		totalWeight += weight
		totalSat += sat * weight

		// Consider low saturation pixels as gray
		if (sat < 0.1) {
			hueRegions.gray.sum += sat * weight
			hueRegions.gray.weight += weight
			hueRegions.gray.max = Math.max(hueRegions.gray.max, sat)
			continue
		}

		// Get color region for this hue
		const [color1, color2, fraction] = hueToColor(hue)
		if (!color1 || !color2) continue

		// Weight the contribution to each adjacent color
		if (fraction < 0.5) {
			hueRegions[color1].sum += sat * weight
			hueRegions[color1].weight += weight
			hueRegions[color1].max = Math.max(hueRegions[color1].max, sat)
		} else {
			hueRegions[color2].sum += sat * weight
			hueRegions[color2].weight += weight
			hueRegions[color2].max = Math.max(hueRegions[color2].max, sat)
		}
	}

	// Calculate averages and percentages
	const totalAvgSat = totalSat / totalWeight
	const defaultStats = { avgSaturation: 0, maxSaturation: 0, percentage: 0 }
	const hueStats = Object.fromEntries(
		["red", "orange", "yellow", "green", "aqua", "blue", "purple", "magenta", "gray"].map(
			(color) => [color, { ...defaultStats }],
		),
	) as Record<HueColor, ColorStats>

	for (const [hue, data] of Object.entries(hueRegions)) {
		hueStats[hue as HueColor] = {
			avgSaturation: data.weight > 0 ? data.sum / data.weight : 0,
			maxSaturation: data.max,
			percentage: totalWeight > 0 ? data.weight / totalWeight : 0,
		}
	}

	const histogram = computeHistogram(luminances)
	const lumPercentiles = computePercentiles(histogram)

	return {
		totalAvgSat,
		totalAvgLum: totalLum / Math.max(totalLumWeight, 0.00001),
		hueStats,
		lumPercentiles,
	}
}

export function getNeighborHueSat(hue: string, refSatStats: ImageSatLumStats): number {
	const hueRegions = ["red", "orange", "yellow", "green", "aqua", "blue", "purple", "magenta"]
	const hueIndex = hueRegions.indexOf(hue)
	const prevHue = hueRegions[(hueIndex - 1 + hueRegions.length) % hueRegions.length] as string
	let nextHue = hueRegions[(hueIndex + 1) % hueRegions.length] as string
	if (hue === "green" && refSatStats.hueStats[nextHue].percentage < 0.00001) {
		nextHue = "blue"
	}
	const aveSat =
		(refSatStats.hueStats[prevHue].avgSaturation +
			refSatStats.hueStats[nextHue].avgSaturation) *
		0.5

	return aveSat
}

export function maintainOrangeRedSaturation(
	adjustments: Partial<Adjustments>,
): Partial<Adjustments> {
	const totalSat = (adjustments.saturation ?? 0) + (adjustments.vibrance ?? 0) * 0.5
	const redSat = adjustments.saturationRed ?? 0
	const orangeSat = adjustments.saturationOrange ?? 0

	const threshold = -0.3
	if (totalSat + redSat < threshold && redSat < 0) {
		logColorMatch(
			"[maintainOrangeRedSaturation] red from",
			redSat,
			"to",
			Math.min(0, threshold - totalSat),
		)
		adjustments.saturationRed = Math.min(0, threshold - totalSat)
	}
	if (totalSat + orangeSat < threshold && orangeSat < 0) {
		logColorMatch(
			"[maintainOrangeRedSaturation] orange from",
			orangeSat,
			"to",
			Math.min(0, threshold - totalSat),
		)
		adjustments.saturationOrange = Math.min(0, threshold - totalSat)
	}
	return adjustments
}

export function reduceHighHSLSaturation(options: {
	adjustments: Partial<Adjustments>
	refSatStats: ImageSatLumStats
	pixelWeights: number[]
	toneMapInvBefore: Float32Array
	baseRenderOptions: RenderOptions
}): Partial<Adjustments> {
	const { adjustments, refSatStats, pixelWeights, toneMapInvBefore, baseRenderOptions } = options
	const threshold = 0.18

	const hueRegions = ["red", "orange", "yellow", "green", "aqua", "blue", "purple", "magenta"]
	let currentSrgb: Float32Array | undefined = undefined
	for (const hue of hueRegions) {
		const satKey = `saturation${capitalize(hue)}`
		const satParameter = adjustments[satKey] ?? 0

		// For larger satParameter, reduce it if reference is less saturated for this hue
		if (satParameter < threshold) {
			continue
		}

		const refStat = refSatStats.hueStats[hue]
		if (!refStat) {
			continue
		}

		if (!currentSrgb) {
			currentSrgb = renderCpu(toneMapInvBefore, adjustments, {
				...baseRenderOptions,
				inputColorSpace: ColorSpaceCpu.toneMapInversed,
				outColorSpace: ColorSpaceCpu.sRGB,
			})
		}
		const currentSatStats = getSaturationStats(currentSrgb, pixelWeights)
		const refSat = refSatStats.hueStats[hue].avgSaturation
		const currentSat = currentSatStats.hueStats[hue].avgSaturation
		const deltaSat = refSat + 0.05 - currentSat

		if (deltaSat < 0.0) {
			const satAdj = deltaSat / 0.6
			adjustments[satKey] = Math.min(Math.max(satParameter + satAdj, threshold), 1)
			logColorMatch(`[reduceHighSaturation] ${hue}`, {
				refSat,
				currentSat,
				deltaSat,
				satParameter,
				satAdj,
				satAdjusted: adjustments[satKey],
			})
		}
	}

	return adjustments
}

export function adjustForLowSatRef(options: {
	adjustments: Partial<Adjustments>
	refSatStats: ImageSatLumStats
	pixelWeights: number[]
	toneMapInvBefore: Float32Array
	baseRenderOptions: RenderOptions
}): Partial<Adjustments> {
	const { adjustments, refSatStats, pixelWeights, toneMapInvBefore, baseRenderOptions } = options
	let coldTonePercentage = 0
	for (const hue of ["green", "aqua", "blue", "purple"]) {
		coldTonePercentage += refSatStats.hueStats[hue].percentage
	}
	logColorMatch("[coldTonePercentage]", coldTonePercentage)

	if (
		refSatStats.hueStats.gray.percentage > 0.6 &&
		refSatStats.totalAvgSat < 0.2 &&
		coldTonePercentage < 0.13
	) {
		const currentSrgb = renderCpu(toneMapInvBefore, adjustments, {
			...baseRenderOptions,
			inputColorSpace: ColorSpaceCpu.toneMapInversed,
			outColorSpace: ColorSpaceCpu.sRGB,
		})
		const currentSatStats = getSaturationStats(currentSrgb, pixelWeights)

		for (const hue in refSatStats.hueStats) {
			if (hue === "gray") {
				continue
			}

			const refStat = refSatStats.hueStats[hue]
			const currentStat = currentSatStats.hueStats[hue]
			if (currentStat.percentage < 0.00001) {
				continue
			}

			const currentSat = currentStat.avgSaturation
			let refSat = refStat.avgSaturation
			if (refStat.percentage < 0.00001) {
				refSat = getNeighborHueSat(hue, refSatStats)
			}

			const deltaSat = refSat - currentSat
			const satKey = `saturation${capitalize(hue)}`
			const saturation = adjustments[satKey] ?? 0

			if (deltaSat >= 0.0 || saturation <= -1) {
				continue
			}

			const satAdj = (deltaSat / currentSat) * (1 + saturation)
			if (refStat.percentage < 0.05) {
				const maxSat = 1
				let scale = 1 - refStat.percentage * 10
				const scale2 = clamp((0.13 - coldTonePercentage) * 10, 0, 1)
				// usually skin and lip colors are not desaturated too much
				if (hue == "red" || hue == "magenta" || hue == "orange") {
					scale *= Math.min(0.5, scale2)
				} else {
					scale *= scale2
				}
				// else if (refStat.percentage < 0.004) {
				// 	// no such colors in reference, fully desaturated
				// 	maxSat = -1 + refStat.maxSaturation
				// }
				adjustments[satKey] = Math.min(Math.max(saturation + satAdj * scale, -1), maxSat)
			}
			logColorMatch(`[satGray] ${hue}`, {
				refStat,
				currentStat,
				satAdj,
				saturation: adjustments[satKey],
			})
		}
	}
	return adjustments
}
